import { useState } from 'react';
import { KeyRound, Wrench, ShieldCheck, CalendarClock, Clock, CheckCircle2 } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { FadeUp, LineReveal } from '../components/Reveal';
import CalendlyEmbed from '../components/CalendlyEmbed';

// No backend on this site — enterprise enquiries open a prefilled email
// instead of posting to an API. Point this at your real sales inbox.
const SALES_EMAIL = process.env.REACT_APP_SALES_EMAIL || 'info@duliptech.com';

const REGIONS = ['US', 'UK', 'Middle East', 'APAC', 'Other'];
const SIZES = ['1-50', '51-200', '201-1000', '1001-5000', '5000+'];
const EDITIONS = ['', 'Essentials', 'Professional', 'Enterprise', 'Unlimited', 'Other'];
const METHODS = ['email', 'phone', 'video call'];

const initial = { full_name: '', email: '', company: '', job_title: '', country: '', company_size: '', salesforce_edition: '', use_case: '', contact_method: 'email' };

export default function Enterprise() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const subject = `Enterprise enquiry — ${form.company || form.full_name}`;
    const body = [
      `Name: ${form.full_name}`,
      `Email: ${form.email}`,
      `Company: ${form.company}`,
      `Job title: ${form.job_title}`,
      `Country: ${form.country}`,
      `Company size: ${form.company_size}`,
      `Salesforce edition: ${form.salesforce_edition}`,
      `Preferred contact method: ${form.contact_method}`,
      '',
      'Use case:',
      form.use_case,
    ].join('\n');
    window.location.href = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  const inputCls = "w-full rounded-xl border border-[#10282C]/12 bg-white px-4 py-3 text-[14px] text-[#10282C] outline-none focus:border-[#00918F] placeholder:text-[#10282C]/30";

  return (
    <div>
      <section className="bg-[#10282C] grain relative overflow-hidden pt-36 pb-24">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00C5C2]">{t('contactPage.kicker')}</p>
          <h1 className="mt-5 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-3xl">
            <LineReveal lines={[t('contactPage.title')]} />
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">{t('contactPage.sub')}</p>
          <p className="mt-5 inline-flex items-center gap-2 text-[13px] font-mono text-[#00C5C2]">
            <Clock size={13} /> {t('contactPage.responseTime')}
          </p>
        </div>
      </section>

      <section className="py-24 bg-[#F6F8F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-5">
            {[
              { icon: KeyRound, title: t('contactPage.byokTitle'), desc: t('contactPage.byokDesc') },
              { icon: Wrench, title: t('contactPage.implTitle'), desc: t('contactPage.implDesc') },
              { icon: ShieldCheck, title: t('contactPage.secTitle'), desc: t('contactPage.secDesc') },
            ].map(({ icon: Icon, title, desc }, i) => (
              <FadeUp key={title} delay={i * 0.08} className="rounded-2xl bg-white border border-[#10282C]/8 p-7">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#00918F]/10 text-[#00918F]">
                  <Icon size={20} strokeWidth={1.8} />
                </span>
                <h2 className="mt-4 font-display text-lg font-semibold text-[#10282C]">{title}</h2>
                <p className="mt-2 text-sm text-[#10282C]/60 leading-relaxed">{desc}</p>
              </FadeUp>
            ))}
            <FadeUp className="rounded-2xl bg-[#10282C] p-7" data-testid="demo-booking-teaser">
              <span className="inline-flex items-center gap-2 text-[#00C5C2] text-sm font-semibold">
                <CalendarClock size={17} /> {t('contactPage.demoTitle')}
              </span>
              <p className="mt-2 text-[13px] text-white/55">{t('contactPage.demoScroll')}</p>
              <a
                href="#book-demo"
                data-testid="demo-booking-scroll-link"
                className="mt-4 btn-teal inline-flex px-5 py-2.5 rounded-full text-[13px] font-semibold"
              >
                {t('contactPage.demoCta')}
              </a>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <FadeUp className="rounded-3xl bg-white border border-[#10282C]/8 p-8 sm:p-10 shadow-[0_24px_60px_-30px_rgba(16,40,44,0.2)]">
              {sent ? (
                <div className="py-16 text-center" data-testid="contact-success-message">
                  <CheckCircle2 size={44} className="mx-auto text-[#00918F]" strokeWidth={1.5} />
                  <p className="mt-5 font-display text-xl font-semibold text-[#10282C]">{t('contactPage.success')}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="enterprise-contact-form">
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.fullName')} *</label>
                    <input data-testid="contact-input-name" required value={form.full_name} onChange={set('full_name')} className={`mt-1.5 ${inputCls}`} style={{ transition: 'border-color 0.2s ease' }} />
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.email')} *</label>
                    <input data-testid="contact-input-email" required type="email" value={form.email} onChange={set('email')} className={`mt-1.5 ${inputCls}`} style={{ transition: 'border-color 0.2s ease' }} />
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.company')} *</label>
                    <input data-testid="contact-input-company" required value={form.company} onChange={set('company')} className={`mt-1.5 ${inputCls}`} style={{ transition: 'border-color 0.2s ease' }} />
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.jobTitle')}</label>
                    <input data-testid="contact-input-jobtitle" value={form.job_title} onChange={set('job_title')} className={`mt-1.5 ${inputCls}`} style={{ transition: 'border-color 0.2s ease' }} />
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.country')} *</label>
                    <select data-testid="contact-select-country" required value={form.country} onChange={set('country')} className={`mt-1.5 ${inputCls}`}>
                      <option value="" disabled>{t('common.selectPlaceholder')}</option>
                      {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.companySize')}</label>
                    <select data-testid="contact-select-size" value={form.company_size} onChange={set('company_size')} className={`mt-1.5 ${inputCls}`}>
                      <option value="">{t('common.selectPlaceholder')}</option>
                      {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.sfEdition')}</label>
                    <select data-testid="contact-select-edition" value={form.salesforce_edition} onChange={set('salesforce_edition')} className={`mt-1.5 ${inputCls}`}>
                      {EDITIONS.map((ed) => <option key={ed} value={ed}>{ed || t('common.selectPlaceholder')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.contactMethod')}</label>
                    <select data-testid="contact-select-method" value={form.contact_method} onChange={set('contact_method')} className={`mt-1.5 ${inputCls}`}>
                      {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('contactPage.useCase')} *</label>
                    <textarea data-testid="contact-input-usecase" required rows={4} value={form.use_case} onChange={set('use_case')} className={`mt-1.5 ${inputCls} resize-none`} style={{ transition: 'border-color 0.2s ease' }} />
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      data-testid="contact-submit-btn"
                      type="submit"
                      className="btn-coral w-full sm:w-auto px-8 py-3.5 rounded-full text-[15px] font-semibold"
                    >
                      {t('contactPage.send')}
                    </button>
                  </div>
                </form>
              )}
            </FadeUp>
          </div>
        </div>
      </section>

      <section id="book-demo" className="py-24 bg-white border-t border-[#10282C]/6">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[12px] font-mono uppercase tracking-[0.2em] text-[#00918F]">{t('contactPage.demoKicker')}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#10282C]">{t('contactPage.demoTitle')}</h2>
            <p className="mt-3 text-base text-[#10282C]/60 leading-relaxed max-w-xl">{t('contactPage.demoLead')}</p>
            <div className="mt-10">
              <CalendlyEmbed />
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
